/**
 * Discussions — the browser's keyed writes, through SoundGuys' proxies (contract §7:
 * `pages/api/owner-notes/discussions/*`), each with the session's bearer. The proxies turn the
 * bearer into DealHunt's signed identity (`signedIdentityFromBearer`); a signed-out or lapsed
 * session answers 401 `auth_required` / `auth_invalid` → `isSignInError`, and the caller opens
 * the in-page sign-in dialog. The send and the XHR upload are `lib/owner-notes/keyed-api`'s,
 * shared with Help & support.
 *
 * Errors are `OwnerApiError` with DealHunt's reader-safe `{ error, message }`, like every owner
 * write, so a 429 budget or a 4xx validation line is shown as DealHunt words it. Every write
 * forgets the shared `/me` before and after (Directive 0d81c597): a first post mints a claimed
 * profile.
 */
import { bearerAuthHeaders, bearerHeaders } from 'lib/owner-notes/api'
import type { OwnerCredential } from 'lib/owner-notes/auth-bridge'
import {
  isSignInError,
  keyedJson,
  keyedSend,
  uploadPhoto,
  type KeyedUpload,
} from 'lib/owner-notes/keyed-api'
import type {
  DiscussionCreateBody,
  DiscussionCreateResponse,
  DiscussionMediaResponse,
  DiscussionPatchBody,
  DiscussionThread,
} from './discussions-types'

/** Kept here for the writer's imports; the rule is `lib/owner-notes/keyed-api`'s. */
export { isSignInError }

export type MediaUpload = KeyedUpload<DiscussionMediaResponse['media']>

/**
 * One photo to DealHunt's media store (multipart field `photo`), with upload progress. The
 * answer's `status` is `public` or `pending` (an untrusted author's image waits for a moderator —
 * contract §0).
 */
function uploadMedia(
  credential: OwnerCredential,
  file: File,
  onProgress: (fraction: number) => void
): MediaUpload {
  return uploadPhoto('/discussions/media', credential, file, onProgress, body => {
    const answer = body as Partial<DiscussionMediaResponse> | null
    const media = answer?.media
    if (!media || typeof media.id !== 'string' || typeof media.url !== 'string') {
      return null
    }
    // DealHunt's line for a pending photo may sit on the media or beside it.
    const message = [media.message, answer?.message].find(
      (m): m is string => typeof m === 'string' && m.trim().length > 0
    )
    return message ? { ...media, message } : media
  })
}

export const discussionsApi = {
  /** `POST /discussions` → 201 `{ discussion, url }`; `url` is the thread's canonical path. */
  create: (
    credential: OwnerCredential,
    body: DiscussionCreateBody
  ): Promise<DiscussionCreateResponse> =>
    keyedSend('/discussions', keyedJson('POST', credential, body)),

  /**
   * The feed's "Ask <handle>" (feed plan slice 4): the same `POST /discussions`, credential
   * optional — null = a signed-out reader's question under the owner-notes device cookie (the
   * proxy forwards it without an identity; 401 `device_unknown` → mint and send again). Always a
   * JSON content type, like the vote.
   */
  ask: (
    credential: OwnerCredential | null,
    body: DiscussionCreateBody
  ): Promise<DiscussionCreateResponse> =>
    keyedSend('/discussions', {
      method: 'POST',
      headers: credential ? bearerHeaders(credential) : { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  /** `PATCH /discussions/:id` — 409 `gear_locked` once answered and the gear differs. */
  update: (
    credential: OwnerCredential,
    id: string,
    body: DiscussionPatchBody
  ): Promise<{ discussion: DiscussionThread }> =>
    keyedSend(`/discussions/${encodeURIComponent(id)}`, keyedJson('PATCH', credential, body)),

  /** `DELETE /discussions/:id` → 204. */
  remove: (credential: OwnerCredential, id: string): Promise<null> =>
    keyedSend(`/discussions/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: bearerAuthHeaders(credential),
    }),

  uploadMedia,
}
